import clsx from "clsx";
import { FC, useCallback, useEffect, useState } from "react";

import { AuthApi, DATA_E2E_KEY, type IRefreshTtlInfoDto } from "@ryde/shared";

import { StyledSessionTtlPanel } from "./controls.panels.styled";
import { formatTtlMs } from "./session-expiration-panel.utils";

export const SessionExpirationPanel: FC = () => {
  const [info, setInfo] = useState<IRefreshTtlInfoDto | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  const load = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError("");

      const data = await AuthApi.sessionRefreshTtlInfo();

      setInfo(data ?? null);
    } catch {
      setError("Failed to load token TTL");
      setInfo(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const sourceSuffix = ((): string => {
    if (info?.source === "company") {
      return "(by company)";
    }

    if (info?.source === "config") {
      return "(by config)";
    }

    return "";
  })();

  const getValueClassName = (): string =>
    clsx("ttl-value", {
      loading: isLoading,
      error: !!error,
    });

  const getAccessValueText = (): string => {
    if (isLoading) {
      return "Loading…";
    }

    if (error || !info) {
      return "—";
    }

    return formatTtlMs(info.accessTokenExpiresInMs);
  };

  const getValueText = (): string => {
    if (isLoading) {
      return "Loading…";
    }

    if (error) {
      return "—";
    }

    if (!info) {
      return "—";
    }

    return `${formatTtlMs(info.expiresInMs)} ${sourceSuffix}`.trim();
  };

  return (
    <StyledSessionTtlPanel>
      <h2 {...{ [DATA_E2E_KEY]: "session-ttl-title" }}>Session token TTL</h2>
      <div className="ttl-row">
        <span
          className="ttl-label"
          {...{ [DATA_E2E_KEY]: "session-ttl-access-label" }}
        >
          Access token expires in:
        </span>
        <span
          className={getValueClassName()}
          {...{ [DATA_E2E_KEY]: "session-ttl-access-value" }}
        >
          {getAccessValueText()}
        </span>
      </div>
      <div className="ttl-row">
        <span
          className="ttl-label"
          {...{ [DATA_E2E_KEY]: "session-ttl-label" }}
        >
          Refresh token (min across companies):
        </span>
        <span
          className={getValueClassName()}
          {...{ [DATA_E2E_KEY]: "session-ttl-value" }}
        >
          {getValueText()}
        </span>
      </div>
      {info?.source === "company" &&
        (info.companyName !== undefined || info.companyId !== undefined) && (
          <div
            className="ttl-meta"
            {...{ [DATA_E2E_KEY]: "session-ttl-company" }}
          >
            Winning company param
            {info.companyName !== undefined ? `: ${info.companyName}` : ""}
            {info.companyId !== undefined ? ` (id ${info.companyId})` : ""}
          </div>
        )}
      {info?.source === "config" && !error && !isLoading && (
        <div
          className="ttl-meta"
          {...{ [DATA_E2E_KEY]: "session-ttl-config-hint" }}
        >
          Env JWT refresh TTL applies (no valid per-company coreRefreshTokenTTL
          among your role companies).
        </div>
      )}
      {error && (
        <div
          className="ttl-meta ttl-meta--error"
          {...{ [DATA_E2E_KEY]: "session-ttl-error" }}
        >
          {error}
        </div>
      )}
    </StyledSessionTtlPanel>
  );
};
